import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import PreventeBanner from '../components/home/PreventeBanner';
import QuickNav from '../components/home/QuickNav';
import ModelShowcase from '../components/home/ModelShowcase';
import PreorderModels from '../components/home/PreorderModels';
import ImpactStats from '../components/home/ImpactStats';
import PersonaSegmentation from '../components/home/PersonaSegmentation';
import SavingsSimulator from '../components/home/SavingsSimulator';
import ProxyPowerSection from '../components/home/ProxyPowerSection';
import ServicesEcosystem from '../components/home/ServicesEcosystem';
import FieldHighlights from '../components/home/FieldHighlights';
import Partners from '../components/home/Partners';
import ProxyFleetPromo from '../components/home/ProxyFleetPromo';
import InvestCTA from '../components/home/InvestCTA';
import PressPreview from '../components/home/PressPreview';
import BlogPreview from '../components/home/BlogPreview';
import FinalCTA from '../components/home/FinalCTA';
import { useHomeSections } from '../hooks/useHomeSections';

const ORGANIZATION_JSON_LD = `
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Proxy Emobility",
              "url": "https://proxyemobility.com/",
              "logo": "https://proxyemobility.com/proxy-logo.png",
              "description": "Proxy Emobility révolutionne la mobilité en Afrique avec des motos électriques innovantes, des stations de recharge solaires et des solutions de financement flexibles.",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Face Service Nationale D'hygiène, Rue YF 158",
                "addressLocality": "Dakar",
                "addressRegion": "Dakar",
                "addressCountry": "Sénégal"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer service",
                "email": "hello@proxyemobility.com"
              }
            }
          `;

const Home: React.FC = () => {
  useEffect(() => {
    document.title = 'Proxy Emobility | Leader de la Mobilité Électrique en Afrique';
  }, []);

  const cfg = useHomeSections();

  // Ordre par défaut = ordre historique (sert de fallback si la base est indisponible)
  const SECTIONS = useMemo(
    () => [
      { key: 'prevente_banner', node: <PreventeBanner /> },
      { key: 'quick_nav', node: <QuickNav /> },
      { key: 'model_showcase', node: <ModelShowcase /> },
      { key: 'persona_segmentation', node: <PersonaSegmentation /> },
      { key: 'partners', node: <Partners /> },
      { key: 'preorder_models', node: <PreorderModels /> },
      { key: 'impact_stats', node: <ImpactStats /> },
      { key: 'savings_simulator', node: <SavingsSimulator /> },
      { key: 'proxy_power', node: <ProxyPowerSection /> },
      { key: 'services_ecosystem', node: <ServicesEcosystem /> },
      { key: 'field_highlights', node: <FieldHighlights /> },
      { key: 'proxy_fleet_promo', node: <ProxyFleetPromo /> },
      { key: 'invest_cta', node: <InvestCTA /> },
      { key: 'press_preview', node: <PressPreview /> },
      { key: 'blog_preview', node: <BlogPreview /> },
      { key: 'final_cta', node: <FinalCTA /> },
    ],
    []
  );

  // Visibilité + ordre pilotés par l'admin (homepage_sections), fallback sûr.
  const visible = useMemo(
    () =>
      SECTIONS.map((s, i) => ({
        ...s,
        enabled: cfg?.[s.key]?.enabled ?? true,
        order: cfg?.[s.key]?.order ?? i + 1,
      }))
        .filter((s) => s.enabled)
        .sort((a, b) => a.order - b.order),
    [SECTIONS, cfg]
  );

  return (
    <div>
      <Helmet>
        <title>Proxy Emobility — Motos électriques et stations solaires au Sénégal</title>
        <meta
          name="description"
          content="Proxy Emobility révolutionne la mobilité en Afrique avec des motos électriques innovantes, des stations de recharge solaires et des solutions de financement flexibles."
        />
        <meta
          name="keywords"
          content="mobilité électrique, motos électriques, Afrique, SUNU, Teranga, OMEGA, stations de recharge, PayGo, financement, développement durable, énergie solaire"
        />
        <link rel="canonical" href="https://proxyemobility.com/" />
        <script type="application/ld+json">{ORGANIZATION_JSON_LD}</script>
      </Helmet>

      {visible.map((s) => (
        <React.Fragment key={s.key}>{s.node}</React.Fragment>
      ))}

    </div>
  );
};

export default Home;
